import type { PartnerSummary } from "@redotech/admin-sdk/rpc/schema/partners/shared";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoTable,
  type RedoTableColumn,
  RedoTableEmptyStateContent,
  RedoTableLoadingStateContent,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import {
  RedoTablePaddedCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { RedoAdminRpcClientContext } from "../app/redo-admin-rpc-client-provider";
import { CreatePartnerModal } from "./create-partner-modal";
import { createdColumn } from "./created-column";
import { PartnerStatusBadge } from "./partner-status-badge";

const SEARCH_DEBOUNCE_MS = 300;

const columns: RedoTableColumn<PartnerSummary>[] = [
  {
    key: "name",
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="Name" />,
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell fontWeight="medium" text={row.name} />
    ),
  },
  {
    key: "status",
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="Status" />,
    renderNormalCell: ({ row }) => (
      <RedoTablePaddedCell>
        <PartnerStatusBadge status={row.status} />
      </RedoTablePaddedCell>
    ),
  },
  {
    key: "stores",
    renderHeaderCell: () => (
      <StandardRedoTableHeaderCell headerText="Active stores" />
    ),
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell text={String(row.storeCount)} />
    ),
  },
  {
    key: "users",
    renderHeaderCell: () => (
      <StandardRedoTableHeaderCell headerText="Portal users" />
    ),
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell text={String(row.userCount)} />
    ),
  },
  createdColumn("Created"),
];

export const PartnersPage = memo(function PartnersPage() {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [search]);

  const partnersLoad = useLoad(
    (signal) =>
      rpcClient.listPartners(
        { searchQuery: debouncedSearch.trim() || undefined },
        { signal },
      ),
    [rpcClient, debouncedSearch],
  );
  const partners = partnersLoad.value?.partners ?? [];

  return (
    <Flex dir="column" gap="md" grow={1} w="full">
      <Flex align="center" gap="md" justify="space-between">
        <Flex w="xxs">
          <RedoTextInput
            autoFocus
            fullWidth
            placeholder="Search partners..."
            setValue={setSearch}
            size="sm"
            value={search}
          />
        </Flex>
        <RedoButton
          hierarchy="primary"
          IconLeading={PlusIcon}
          onClick={() => setCreating(true)}
          size="sm"
          text="Create partner"
        />
      </Flex>
      <RedoTable
        emptyStateContent={
          partnersLoad.error ? (
            <RedoTableEmptyStateContent text="Couldn't load partners" />
          ) : !partnersLoad.pending && partners.length === 0 ? (
            <RedoTableEmptyStateContent
              text={debouncedSearch ? "No partners match" : "No partners yet"}
            />
          ) : null
        }
        layout={{ columns, standalone: true }}
        loadingStateContent={
          partnersLoad.pending && !partnersLoad.value ? (
            <RedoTableLoadingStateContent />
          ) : null
        }
        onRowClick={(row) => navigate(`/partners/${row.id}`)}
        rowsOfData={partners}
      />
      {creating && (
        <CreatePartnerModal
          onClose={() => setCreating(false)}
          onCreated={(partner) => navigate(`/partners/${partner.id}`)}
        />
      )}
    </Flex>
  );
});
